import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Chip,
  Paper,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  NavigateBefore,
  NavigateNext,
  Star,
  LocalShipping,
  Restaurant,
  ShoppingBag,
} from '@mui/icons-material';
import { getImageProps } from '../../utils/imageUtils';
import { useCustomTheme } from '../../contexts/ThemeContext';

const HeroSlider = ({ sliderImages = [], restaurant = {} }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || isHovered || sliderImages.length <= 1) {
      console.log('Auto-play paused:', { isPlaying, isHovered, sliderImagesLength: sliderImages.length });
      return;
    }

    console.log('Starting auto-play interval');
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const nextSlide = (prev + 1) % sliderImages.length;
        console.log(`Auto-play: Moving from slide ${prev} to slide ${nextSlide} (total: ${sliderImages.length})`);
        return nextSlide;
      });
    }, 3000); // Change slide every 3 seconds

    return () => {
      console.log('Clearing auto-play interval');
      clearInterval(interval);
    };
  }, [isPlaying, isHovered, sliderImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => {
      const nextSlide = (prev + 1) % sliderImages.length;
      console.log(`Manual next: Moving from slide ${prev} to slide ${nextSlide}`);
      return nextSlide;
    });
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => {
      const prevSlide = (prev - 1 + sliderImages.length) % sliderImages.length;
      console.log(`Manual prev: Moving from slide ${prev} to slide ${prevSlide}`);
      return prevSlide;
    });
  };

  const goToSlide = (index) => {
    console.log(`Manual goTo: Moving to slide ${index}`);
    setCurrentSlide(index);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Preload images for better performance
  useEffect(() => {
    if (sliderImages && sliderImages.length > 0) {
      // Reset current slide when images change
      setCurrentSlide(0);
      
      const preloadImages = () => {
        const imagePromises = sliderImages.map((image) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load image: ${image.image_url}`));
            img.src = image.image_url;
          });
        });

        Promise.all(imagePromises)
          .then(() => {
            console.log('All slider images preloaded successfully');
            setImagesLoaded(true);
          })  
          .catch((error) => {
            console.error('Error preloading images:', error);
            setImagesLoaded(true); // Still show slider even if some images fail
          });
      };

      preloadImages();
    } else {
      setImagesLoaded(true);
    }
  }, [sliderImages]);

  // Show loading state while images are being preloaded
  if (!imagesLoaded) {
    return (
      <Box
        sx={{
          marginTop:'0px !important',
          position: 'relative',
          minHeight: { xs: '60vh', md: '70vh' },
          maxHeight: { xs: '60vh', md: '70vh' },
          mt: { xs: 7, md: 8 },
          background: isDarkMode
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
            : 'linear-gradient(135deg, #2d1b14 0%, #8B4513 50%, #2d1b14 100%)',
          color: 'white',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: 'white', opacity: 0.8 }}>
            Loading images...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!sliderImages || sliderImages.length === 0) {
    // Fallback to static hero section if no slider images
    return (
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '50vh', sm: '60vh', md: '70vh' },
          maxHeight: { xs: '50vh', sm: '60vh', md: '70vh' },
          mt: { xs: 7, sm: 7, md: 8 },
          background: isDarkMode
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
            : 'linear-gradient(135deg, #2d1b14 0%, #8B4513 50%, #2d1b14 100%)',
          color: 'white',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Container maxWidth="lg" sx={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, md: 6 },
        }}>
          <Box sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '100%',
          }}>
            {/* Banner only - no text content */}
          </Box>
        </Container>
      </Box>
    );
  }

  // Ensure currentSlide is within bounds
  const safeCurrentSlide = Math.max(0, Math.min(currentSlide, sliderImages.length - 1));
  const currentImage = sliderImages[safeCurrentSlide];

  // Debug logging
  console.log('Slider Debug:', {
    currentSlide,
    safeCurrentSlide,
    sliderImagesLength: sliderImages.length,
    currentImage: currentImage ? currentImage.title || 'No title' : 'No image',
    sliderImages: sliderImages.map((img, index) => ({ index, title: img.title || 'No title' }))
  });

  return (
    <Box
      sx={{
        marginTop:'0px !important',
        position: 'relative',
        minHeight: { xs: '50vh', sm: '60vh', md: '70vh' },
        maxHeight: { xs: '50vh', sm: '60vh', md: '70vh' },
        mt: { xs: 7, sm: 7, md: 8 },
        overflow: 'hidden',
        cursor: 'pointer',
        width: '100%',
      cursor: 'default', 
        "&:hover": {
          cursor: 'default'
        }

      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Single Image Display */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${currentImage.image_url})`,
          backgroundSize: { xs: 'cover', sm: 'cover', md: 'cover' },
          backgroundPosition: { xs: 'center center', sm: 'center center', md: 'center center' },
          backgroundRepeat: 'no-repeat',
          transition: 'opacity 0.5s ease-in-out',
          width: '100%',
          height: '100%',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent',
            zIndex: 1,
          }
        }}
      />

      {/* No overlay - images at normal brightness */}

      {/* Content - Perfectly Centered */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: '100%',
        }}>
          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}>
            {/* Banner only - no text content */}
          </Box>
        </Box>
      </Container>

      {/* Navigation Controls */}
      {sliderImages.length > 1 && (
        <>
          {/* Enhanced Previous Button */}
          <IconButton
            onClick={prevSlide}
            sx={{
              position: 'absolute',
              left: { xs: 8, sm: 16, md: 32 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              width: { xs: 40, sm: 48, md: 56 },
              height: { xs: 40, sm: 48, md: 56 },
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.2)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.8)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 6px 25px rgba(0,0,0,0.5)',
                border: '2px solid rgba(255,255,255,0.4)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <NavigateBefore sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' } }} />
          </IconButton>

          {/* Enhanced Next Button */}
          <IconButton
            onClick={nextSlide}
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 16, md: 32 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              width: { xs: 40, sm: 48, md: 56 },
              height: { xs: 40, sm: 48, md: 56 },
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.2)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.8)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 6px 25px rgba(0,0,0,0.5)',
                border: '2px solid rgba(255,255,255,0.4)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <NavigateNext sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' } }} />
          </IconButton>

          {/* Enhanced Play/Pause Button */}
          <IconButton
            onClick={togglePlayPause}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 16, md: 24 },
              right: { xs: 8, sm: 16, md: 24 },
              zIndex: 3,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              width: { xs: 36, sm: 44, md: 48 },
              height: { xs: 36, sm: 44, md: 48 },
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.2)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.8)',
                transform: 'scale(1.1)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
                border: '2px solid rgba(255,255,255,0.4)',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {isPlaying ? <Pause sx={{ fontSize: { xs: '1rem', sm: '1.3rem', md: '1.5rem' } }} /> : <PlayArrow sx={{ fontSize: { xs: '1rem', sm: '1.3rem', md: '1.5rem' } }} />}
          </IconButton>

          {/* Enhanced Dots Indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: { xs: 8, sm: 16, md: 24 },
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 3,
              display: 'flex',
              gap: { xs: 1, sm: 1.5, md: 1.5 },
              bgcolor: 'rgba(0,0,0,0.3)',
              px: { xs: 1.5, sm: 2, md: 2 },
              py: { xs: 0.5, sm: 1, md: 1 },
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {sliderImages.map((_, index) => (
              <Box
                key={index}
                onClick={() => goToSlide(index)}
                sx={{
                  width: { xs: 10, sm: 12, md: 14 },
                  height: { xs: 10, sm: 12, md: 14 },
                  borderRadius: '50%',
                  bgcolor: index === currentSlide ? 'white' : 'rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: index === currentSlide ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                  '&:hover': {
                    bgcolor: index === currentSlide ? 'white' : 'rgba(255,255,255,0.7)',
                    transform: 'scale(1.3)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                  },
                }}
              />
            ))}
          </Box>
        </>
      )}

      {/* Enhanced Slide Counter */}
      {sliderImages.length > 1 && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: { xs: 8, sm: 16, md: 24 },
            right: { xs: 8, sm: 16, md: 24 },
            zIndex: 3,
            px: { xs: 2, sm: 3, md: 3 },
            py: { xs: 1, sm: 1.5, md: 1.5 },
            bgcolor: 'rgba(0,0,0,0.7)',
            color: 'white',
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.9rem', md: '0.9rem' } }}>
            {currentSlide + 1} / {sliderImages.length}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default HeroSlider;